import React, { useState } from "react";
import { track } from "@amplitude/analytics-browser";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { useRouter } from "next/router";

import Icon from "../common/Icon";
import FormikFormLine from "../formik-form-components/FormikFormLine";
import Button from "../common/Button";

import { EMAIL_SCHEMA, getPhoneNumberSchema } from "../../utils/forms";
import { FieldTypes } from "../formik-form-components/FormikFormLine/constants";
import { useAppSelector } from "../../utils/hooks/store";
import { ButtonTypes } from "../common/Button/constants";
import { PRIVACY } from "../../constants/routes";
import {
  formatPhoneNumberInput,
  formatPhoneNumberNoCountryCode,
} from "../../utils/valueFormatting";
import { useSubmitContactForm } from "../../utils/contactForm";
import {
  Card,
  Form,
  Header,
  PrivacyLink,
  ResultInfo,
  SubmitContact,
  UnimportantText,
} from "./ContactPageStyles";
import useTrackGoogleEvent, {
  FILL_OUT_CONTACT_US_FORM,
} from "../../utils/observability/googleTagManager";
import { SUPPORT_PHONE_NUMBER } from "../../constants/support";
import { useUser } from "../../utils/hooks/commonly-stored/users";

interface FormValues {
  email: string;
  phoneNumber: string;
  message: string;
}

const schema = Yup.object().shape({
  email: EMAIL_SCHEMA,
  phoneNumber: getPhoneNumberSchema(true),
  message: Yup.string().required("Message is required"),
});

const FORM_INFO = [
  {
    name: "email",
    label: "Email",
    placeholder: "Enter email address",
    type: FieldTypes.email,
  },
  {
    name: "phoneNumber",
    label: "Phone Number",
    placeholder: "Enter phone number",
    type: FieldTypes.tel,
  },
  {
    name: "message",
    label: "Message",
    placeholder: "Let us know what you need help with...",
    isTextArea: true,
    styleProps: {
      height: "160px",
    },
  },
];
const ContactPage = () => {
  const router = useRouter();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const submitContactForm = useSubmitContactForm();
  const { email, userId } = useAppSelector((state) => state.auth);
  const { user } = useUser(userId);
  const trackGoogleEvent = useTrackGoogleEvent();

  const initialValues = {
    email: email || "",
    phoneNumber: user?.phone_number
      ? formatPhoneNumberNoCountryCode(user?.phone_number)
      : "",
    message: "",
  };

  const sendMessage = (
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>,
  ) => {
    setSent(false);
    setSending(true);
    track("Send message via contact form");
    trackGoogleEvent({
      action: FILL_OUT_CONTACT_US_FORM,
    });
    void submitContactForm(
      formatPhoneNumberInput(values.phoneNumber),
      values.email,
      "FlashPCB Contact form",
      values.message,
      () => {
        setSending(false);
        setSent(true);
        setTimeout(() => setSent(false), 4000);
        resetForm({ values: initialValues });
      },
      () => {
        setSending(false);
      },
    );
  };

  return (
    <Card>
      <Header>
        <Icon
          src={"/icons/mail_red.svg"}
          alt={"contact us"}
          iconDimension={50}
        />
        <div>Contact</div>
      </Header>
      <Formik
        initialValues={initialValues}
        onSubmit={sendMessage}
        validationSchema={schema}
      >
        {({ handleSubmit, isValid, dirty }) => (
          <Form onSubmit={handleSubmit}>
            {FORM_INFO.map((field) => (
              <FormikFormLine
                key={field.name}
                name={field.name}
                label={field.label}
                type={field.type}
                isTextArea={field.isTextArea}
                placeholder={field.placeholder}
                styleProps={field.styleProps}
                disabled={sending}
              />
            ))}
            <SubmitContact>
              <Button
                label={"Send"}
                centerText
                type={ButtonTypes.SUBMIT}
                disabled={!(isValid && dirty) || sending}
              />
              <UnimportantText>
                This site is protected by reCAPTCHA and the Google{" "}
                <a href="https://policies.google.com/privacy">Privacy Policy</a>{" "}
                and{" "}
                <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
                apply.
              </UnimportantText>
            </SubmitContact>
            <ResultInfo>{sent && "Sent!"}</ResultInfo>
          </Form>
        )}
      </Formik>
      <a href={`tel:${SUPPORT_PHONE_NUMBER}`}>{SUPPORT_PHONE_NUMBER}</a>
      <PrivacyLink onClick={() => void router.push(PRIVACY)}>
        Privacy Policy
      </PrivacyLink>
    </Card>
  );
};

export default ContactPage;
